// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import { useEffect } from 'react';
import { useMap } from 'react-map-gl';
import { useAppDispatch } from 'Hooks';
import { SetCanvasSizeAction } from '../mapCommonActions';

// mapbox-gl 1.x only re-measures its canvas on window resize events. Any layout change that
// resizes the map container without one (a toolbar mounting or unmounting above the map, the
// sidebar collapsing) leaves the canvas at its old size: a white strip when the container grows,
// a clipped bottom edge when it shrinks. Observing the container itself covers every such change
// on every page that renders CreateMap, and keeps the cached canvasSize (used by the viewport
// fits) current instead of frozen at mount. (AUT-6500)
const MapResizeObserver = (): null => {
  const { current: mapRef } = useMap();
  const dispatch = useAppDispatch();

  useEffect(() => {
    // jsdom has no ResizeObserver; there the map keeps its mount-time size, as before.
    if (!mapRef || typeof ResizeObserver === 'undefined') return undefined;
    const map = mapRef.getMap();
    // Coalesce every burst of entries into a single reaction per animation frame, and dispatch only
    // when the size actually changed. map.resize() and the canvasSize dispatch both feed layout, so
    // reacting synchronously inside the callback is the shape that can re-enter the observer in the
    // same frame and raise Chrome's "ResizeObserver loop" notice. One rAF reaction per frame plus a
    // compare-before-dispatch means this observer can never originate that loop. (AUT-6757)
    let frame = 0;
    let lastWidth = -1;
    let lastHeight = -1;
    let latestEntry: ResizeObserverEntry | undefined;
    const observer = new ResizeObserver((entries) => {
      [latestEntry] = entries;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = 0;
        map.resize();
        const rect = latestEntry?.contentRect;
        if (!rect) return;
        const { width, height } = rect;
        if (width > 0 && height > 0 && (width !== lastWidth || height !== lastHeight)) {
          lastWidth = width;
          lastHeight = height;
          dispatch(SetCanvasSizeAction([width, height]));
        }
      });
    });
    observer.observe(map.getContainer());
    return () => {
      if (frame) cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [mapRef]);

  return null;
};

export default MapResizeObserver;
